import { Center, Group, Paper, Skeleton, Table, Text } from '@mantine/core';
import { ReactNode } from 'react';
import { FaSort, FaSortDown, FaSortUp } from 'react-icons/fa';
import classes from './GenericTable.module.css';

export interface GenericTableColumn<T> {
  accessor: keyof T;
  title: string;
  sortable?: boolean;
  width?: string | number;
  maxWidth?: string | number;
  wrap?: boolean;
  render?: (record: T) => ReactNode;
}

interface GenericTableProps<T> {
  records: T[];
  columns: GenericTableColumn<T>[];
  loading?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (field: string) => void;
  emptyMessage?: string;
  idAccessor?: keyof T;
  onRowClick?: <T>(row: T) => void;
  minWidth?: number | string;
}

interface SortableHeaderProps {
  field: string;
  label: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (field: string) => void;
}

function SortableHeader({
  field,
  label,
  sortBy,
  sortOrder,
  onSort,
}: SortableHeaderProps) {
  const isActive = sortBy === field;

  const getSortIcon = () => {
    if (!isActive) return <FaSort size={12} />;
    return sortOrder === 'asc' ? (
      <FaSortUp size={12} />
    ) : (
      <FaSortDown size={12} />
    );
  };

  return (
    <Group
      gap="xs"
      style={{ cursor: onSort ? 'pointer' : 'default' }}
      onClick={() => onSort?.(field)}
      wrap="nowrap"
    >
      <Text fz="xs" fw={500}>
        {label}
      </Text>
      {onSort && getSortIcon()}
    </Group>
  );
}

function LoadingRow({ colSpan }: { colSpan: number }) {
  return (
    <Table.Tr>
      {Array.from({ length: colSpan }).map((_, i) => (
        <Table.Td key={i}>
          <Skeleton height={16} />
        </Table.Td>
      ))}
    </Table.Tr>
  );
}

function EmptyState({
  message,
  colSpan,
}: {
  message: string;
  colSpan: number;
}) {
  return (
    <Table.Tr>
      <Table.Td colSpan={colSpan}>
        <Center p="xl">
          <Text c="dimmed" size="md">
            {message}
          </Text>
        </Center>
      </Table.Td>
    </Table.Tr>
  );
}

export function GenericTable<T extends Record<string, any>>({
  records,
  columns,
  loading = false,
  sortBy,
  sortOrder,
  onSort,
  emptyMessage = 'No records found',
  idAccessor = 'id' as keyof T,
  onRowClick,
  minWidth,
}: GenericTableProps<T>) {
  return (
    <Paper withBorder style={{ overflowX: 'auto' }}>
      <Table
        highlightOnHover
        miw={minWidth}
        styles={{
          td: {
            whiteSpace: 'nowrap',
          },
          th: {
            whiteSpace: 'nowrap',
          },
        }}
      >
        <Table.Thead>
          <Table.Tr>
            {columns.map((column) => (
              <Table.Th
                key={String(column.accessor)}
                style={{ width: column.width }}
              >
                {column.sortable ? (
                  <SortableHeader
                    field={String(column.accessor)}
                    label={column.title}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={onSort}
                  />
                ) : (
                  <Text fz="xs" fw={500}>
                    {column.title}
                  </Text>
                )}
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {loading ? (
            <>
              <LoadingRow colSpan={columns.length} />
              <LoadingRow colSpan={columns.length} />
              <LoadingRow colSpan={columns.length} />
            </>
          ) : records.length === 0 ? (
            <EmptyState message={emptyMessage} colSpan={columns.length} />
          ) : (
            records.map((record) => (
              <Table.Tr
                key={String(record[idAccessor])}
                onClick={() => (onRowClick ? onRowClick(record) : undefined)}
                className={onRowClick ? classes.rowClickable : ''}
              >
                {columns.map((column) => (
                  <Table.Td
                    key={String(column.accessor)}
                    style={{
                      whiteSpace: column.wrap ? 'normal' : undefined,
                      maxWidth: column.maxWidth,
                    }}
                  >
                    {column.render ? (
                      column.render(record)
                    ) : (
                      <Text fz="xs">{String(record[column.accessor])}</Text>
                    )}
                  </Table.Td>
                ))}
              </Table.Tr>
            ))
          )}
        </Table.Tbody>
      </Table>
    </Paper>
  );
}

export default GenericTable;
